import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, Link } from 'wouter';
import { Mail, Lock, LogIn, ArrowRight, Chrome, Apple, User, AlertCircle, Phone, Smartphone, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import toast from 'react-hot-toast';

export default function Login() {
  const { 
    signInWithGoogle, 
    signInWithApple, 
    signInWithEmail, 
    signUpWithEmail, 
    signInWithPhone,
    setupRecaptcha,
    user 
  } = useAuth();
  const [, setLocation] = useLocation();
  const [authMode, setAuthMode] = useState<'email' | 'phone'>('email');
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('+91 ');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      setLocation('/');
    }
  }, [user, setLocation]);

  useEffect(() => {
    if (authMode === 'phone') {
      // Small delay to ensure DOM is ready if it was hidden
      const timer = setTimeout(() => {
        setupRecaptcha('recaptcha-container');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authMode, setupRecaptcha]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (authMode === 'email') {
        if (isLogin) {
          await signInWithEmail(email, password);
          toast.success('Welcome back!');
        } else {
          if (!name) {
            toast.error('Please enter your name');
            setLoading(false);
            return;
          }
          await signUpWithEmail(email, password, name);
          toast.success('Account created successfully!');
        }
        setLocation('/');
      } else {
        // Phone Auth
        if (!confirmationResult) {
          // Send OTP
          const trimmedPhone = phoneNumber.trim().replace(/\s+/g, '');
          if (trimmedPhone.length < 10) {
            toast.error('Please enter a valid phone number');
            setLoading(false);
            return;
          }
          const finalPhone = trimmedPhone.startsWith('+') ? trimmedPhone : `+${trimmedPhone}`;
          const result = await signInWithPhone(finalPhone);
          setConfirmationResult(result);
          toast.success('OTP sent to your phone!');
        } else {
          // Verify OTP
          try {
            await confirmationResult.confirm(verificationCode.trim());
            toast.success('Signed in successfully!');
            setLocation('/');
          } catch (confirmError: any) {
            console.error("OTP Confirmation Error:", confirmError);
            if (confirmError.code === 'auth/invalid-verification-code') {
              toast.error('Invalid verification code. Please check and try again.');
            } else if (confirmError.code === 'auth/code-expired') {
              toast.error('Verification code has expired. Please request a new one.');
              setConfirmationResult(null);
              setVerificationCode('');
            } else {
              toast.error(confirmError.message || 'Verification failed. Please try again.');
            }
          }
        }
      }
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') return;
      console.error(error);
      toast.error(error.message || 'Authentication failed');
      // Reset confirmation if it fails on sending OTP
      if (authMode === 'phone' && !confirmationResult) {
        setConfirmationResult(null);
        setVerificationCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (method: 'google' | 'apple') => {
    try {
      if (method === 'google') await signInWithGoogle();
      else await signInWithApple();
      toast.success('Signed in successfully!');
      setLocation('/');
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        // Just silent return if user closed the popup
        return;
      }
      toast.error(error.message || 'Social sign-in failed');
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Don't allow deleting the + prefix if desired, but here we just ensure 
    // we help the user keep it if they start typing
    if (!value.startsWith('+')) {
      value = '+' + value.replace(/\D/g, '');
    }
    setPhoneNumber(value);
  };

  const isIframe = typeof window !== 'undefined' && window.self !== window.top;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] px-4 py-20 font-sans">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white/5 border border-white/5 rounded-[48px] overflow-hidden relative shadow-2xl"
      >
        {/* Background Accent */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-spice-accent/10 blur-[80px] rounded-full -mr-16 -mt-16"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-spice-red/5 blur-[80px] rounded-full -ml-16 -mb-16"></div>

        <div className="p-10 md:p-14 relative z-10">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-serif font-bold tracking-tight text-white mb-3 uppercase">
              {isLogin ? 'Welcome' : 'Create'} <span className="serif italic font-normal text-spice-accent">{isLogin ? 'Back' : 'Account'}</span>
            </h1>
            <p className="text-[9px] text-white/40 font-black uppercase tracking-[0.3em]">
              {isLogin 
                ? 'Authorized Access' 
                : 'Join the Spice Guild'}
            </p>
          </div>

          {isIframe && (
            <div className="mb-8 p-4 bg-spice-accent/10 border border-spice-accent/20 rounded-2xl">
              <p className="text-[9px] font-black uppercase tracking-widest text-spice-accent leading-relaxed">
                <span className="flex items-center gap-2 mb-1 text-white"><AlertCircle className="w-3 h-3" /> Security Alert</span>
                Authentication popups may be blocked in this preview. If sign-in fails, please open the application in a new tab.
              </p>
            </div>
          )}

          {/* Primary Action: Google */}
          <div className="space-y-4 mb-10">
            <button
              onClick={() => handleSocialSignIn('google')}
              className="w-full flex items-center justify-center gap-4 px-4 py-5 bg-white text-[#09090b] rounded-2xl transition-all font-black uppercase tracking-[0.2em] text-[10px] hover:scale-[1.02] active:scale-95 shadow-xl shadow-white/5"
            >
              <Chrome className="w-4 h-4" />
              <span>Continue with Google</span>
            </button>
            <button
              onClick={() => handleSocialSignIn('apple')}
              className="w-full flex items-center justify-center gap-4 px-4 py-5 bg-white/5 border border-white/10 text-white rounded-2xl transition-all font-black uppercase tracking-[0.2em] text-[10px] hover:bg-white/10"
            >
              <Apple className="w-4 h-4" />
              <span>Continue with Apple</span>
            </button>
          </div>

          <div className="relative my-10">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/5"></div>
            </div>
            <div className="relative flex justify-center text-[8px] font-black uppercase tracking-[0.5em]">
              <span className="px-4 bg-[#09090b] text-white/20">or security credentials</span>
            </div>
          </div>

          {/* Type Toggle */}
          <div className="flex bg-white/5 p-1 rounded-2xl mb-8 border border-white/5">
            <button
              onClick={() => {
                setAuthMode('email');
                setConfirmationResult(null);
              }}
              className={`flex-1 py-3 text-[9px] font-black uppercase tracking-[0.2em] transition-all rounded-xl ${
                authMode === 'email' ? 'bg-white/10 text-white shadow-sm ring-1 ring-white/10' : 'text-white/30 hover:text-white/50'
              }`}
            >
              Email
            </button>
            <button
              onClick={() => {
                setAuthMode('phone');
              }}
              className={`flex-1 py-3 text-[9px] font-black uppercase tracking-[0.2em] transition-all rounded-xl ${
                authMode === 'phone' ? 'bg-white/10 text-white shadow-sm ring-1 ring-white/10' : 'text-white/30 hover:text-white/50'
              }`}
            >
              Phone
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <AnimatePresence mode="wait">
              {authMode === 'email' ? (
                <motion.div
                  key="email-fields"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  {!isLogin && (
                    <div>
                      <label className="block text-[8px] font-black uppercase tracking-[0.2em] text-white/30 mb-2 ml-1">Full Name</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-white/20 group-focus-within:text-spice-accent transition-colors">
                          <User className="w-4 h-4" />
                        </div>
                        <input
                          type="text"
                          required={!isLogin && authMode === 'email'}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="block w-full pl-12 pr-5 py-4 bg-white/5 border border-white/5 rounded-2xl focus:bg-white/10 focus:border-spice-accent/30 outline-none transition-all text-sm font-medium text-white placeholder:text-white/10"
                          placeholder="Your Name"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-[8px] font-black uppercase tracking-[0.2em] text-white/30 mb-2 ml-1">Email Address</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-white/20 group-focus-within:text-spice-accent transition-colors">
                        <Mail className="w-4 h-4" />
                      </div>
                      <input
                        type="email"
                        required={authMode === 'email'}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-12 pr-5 py-4 bg-white/5 border border-white/5 rounded-2xl focus:bg-white/10 focus:border-spice-accent/30 outline-none transition-all text-sm font-medium text-white placeholder:text-white/10"
                        placeholder="name@empire.com"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2 ml-1">
                      <label className="block text-[8px] font-black uppercase tracking-[0.2em] text-white/30">Password</label>
                      {isLogin && (
                        <button type="button" className="text-[8px] font-black uppercase tracking-[0.2em] text-spice-accent hover:text-white transition-colors">Recover Account?</button>
                      )}
                    </div>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-white/20 group-focus-within:text-spice-accent transition-colors">
                        <Lock className="w-4 h-4" />
                      </div>
                      <input
                        type="password"
                        required={authMode === 'email'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-12 pr-5 py-4 bg-white/5 border border-white/5 rounded-2xl focus:bg-white/10 focus:border-spice-accent/30 outline-none transition-all text-sm font-medium text-white placeholder:text-white/10"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="phone-fields"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-[8px] font-black uppercase tracking-[0.2em] text-white/30 mb-2 ml-1">Phone Number</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-white/20 group-focus-within:text-spice-accent transition-colors">
                        <Phone className="w-4 h-4" />
                      </div>
                      <input
                        type="tel"
                        required={authMode === 'phone'}
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        disabled={!!confirmationResult}
                        className="block w-full pl-12 pr-5 py-4 bg-white/5 border border-white/5 rounded-2xl focus:bg-white/10 focus:border-spice-accent/30 outline-none transition-all text-sm font-black tracking-widest text-white disabled:opacity-50 placeholder:text-white/10"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>

                  {confirmationResult && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                    >
                      <label className="block text-[8px] font-black uppercase tracking-[0.2em] text-white/30 mb-2 ml-1">Verification Code</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-white/20 group-focus-within:text-spice-accent transition-colors">
                          <Smartphone className="w-4 h-4" />
                        </div>
                        <input
                          type="text"
                          required={!!confirmationResult}
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          className="block w-full pl-12 pr-5 py-4 bg-white/5 border border-white/5 rounded-2xl focus:bg-white/10 focus:border-spice-accent/30 outline-none transition-all text-sm font-black tracking-[0.5em] text-center text-white placeholder:text-white/10"
                          placeholder="000000"
                        />
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <div id="recaptcha-container" className="flex justify-center"></div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-spice-accent text-white py-6 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] hover:scale-[1.02] active:scale-95 transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden group shadow-2xl shadow-spice-accent/20"
            >
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div 
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
                  />
                ) : (
                  <motion.div 
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-3"
                  >
                    <span>
                      {authMode === 'phone' 
                        ? (confirmationResult ? 'Authenticate' : 'Request OTP')
                        : (isLogin ? 'Enter The Guild' : 'Begin Journey')}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </form>

          {confirmationResult && (
            <button 
              onClick={() => {
                setConfirmationResult(null);
                setVerificationCode('');
              }}
              className="w-full mt-6 text-[8px] font-black uppercase tracking-[0.2em] text-spice-accent hover:text-white transition-colors"
            >
              Edit Identification Number
            </button>
          )}

          <div className="mt-12 text-center">
            <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">
              {isLogin ? "New to the Guild?" : "Already Authorized?"}{' '}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-spice-accent font-black hover:text-white transition-colors ml-2"
              >
                {isLogin ? 'Apply for Membership' : 'Sign In Now'}
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
